// src\layout\customer\ProjectInvite\ProjectInviteForm.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, // Company
  ClipboardList, // Project Name
  BadgeCheck, // Ducktail ID
  Phone, // Contact
  MapPin, // Location
  CalendarDays, // Dates
  IndianRupee, // Budget
  Mail, // Email
  CheckCircle, // Accept
  XCircle, // Decline
  Loader2, // Loading
  UserCircle, // Builder Name Icon
} from "lucide-react";
import useProjectStatus from "../../../store/builders/useProjectStatus"; // Shared store for project listings
import useProjectEnqStore from "../../../store/customer/useProjectEnqStore"; // Customer-specific actions
import { toast } from "react-hot-toast";

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount == null || isNaN(Number(amount))) return "N/A";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount));
};

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    return "Invalid Date";
  }
};

const ProjectInviteForm = () => {
  const navigate = useNavigate();
  const {
    projects: pendingProjects, // Renamed for clarity
    isLoading: projectsLoading,
    fetchProjects,
    error: projectsError,
  } = useProjectStatus();

  const {
    updateProjectStatus: customerUpdateStatus,
    isLoading: statusLoading,
    error: statusError,
  } = useProjectEnqStore();

  const [processingProjectId, setProcessingProjectId] = useState(null);

  useEffect(() => {
    // Fetch projects with 'pending_approval' status, which means customer needs to approve/decline
    fetchProjects("pending_approval");
  }, [fetchProjects]);

  const handleAction = async (project, action) => {
    if (processingProjectId) return; // Prevent multiple clicks while one is processing

    setProcessingProjectId(project.id);
    try {
      // If action is 'decline', provide a default reason.
      // For 'accept', the comment/reason remains an empty string or null as appropriate.
      const reasonForAction =
        action === "decline" ? "Invitation declined by customer." : "";
      const response = await customerUpdateStatus(
        project.id,
        action,
        reasonForAction
      );

      if (response.success) {
        toast.success(
          `Project invitation ${
            action === "accept" ? "accepted" : "declined"
          } successfully!`
        );
        // Navigate based on action
        if (action === "accept") {
          navigate("/customer/livecard");
        } else {
          navigate("/customer/projectinvite");
        }
        fetchProjects("pending_approval"); // Refresh the list of pending invites
      } else {
        toast.error(
          response.message || `Failed to ${action} project invitation.`
        );
      }
    } catch (error) {
      console.error(`Error ${action}ing project:`, error);
      toast.error(
        `An unexpected error occurred while trying to ${action} the invitation.`
      );
    } finally {
      setProcessingProjectId(null);
    }
  };

  const isLoading = projectsLoading || statusLoading;

  if (projectsError) {
    return <p className="text-center text-red-500 p-4">{projectsError}</p>;
  }

  if (isLoading && pendingProjects.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!isLoading && pendingProjects.length === 0) {
    return (
      <div className="text-center text-gray-600 p-8 text-sm min-h-[60vh] flex flex-col justify-center items-center">
        <img
          src="/assets/check.png"
          alt="No Invites"
          className="w-20 h-auto mb-4 opacity-70"
        />
        <p className="text-lg font-semibold">No Pending Project Invitations</p>
        <p>You currently have no new project invitations from builders.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {pendingProjects.map((project) => (
          <div
            key={project.id}
            className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 flex flex-col transition-all duration-300 hover:shadow-xl"
          >
            {/* Builder Information */}
            <div className="mb-3 pb-3 border-b border-gray-100">
              <div className="flex items-center gap-3 mb-1.5 min-w-0">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden border border-blue-200 flex-shrink-0">
                  {/* Placeholder for builder logo/image */}
                  <Building2 className="w-5 h-5 text-blue-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3
                    className="text-sm sm:text-base font-semibold text-gray-800 truncate sm:whitespace-normal sm:break-words"
                    title={project.builder_company}
                  >
                    {project.builder_company || "N/A"}
                  </h3>
                  {project.builder_name && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <UserCircle className="w-3 h-3 text-gray-400" />
                      <span className="truncate">{project.builder_name}</span>
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2 text-gray-600">
                  <BadgeCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                  <span>
                    Ducktail ID: {project.builder_ducktail_id || "N/A"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span>
                    {project.builder_contact
                      ? `+91 ${project.builder_contact}`
                      : "N/A"}
                  </span>
                </div>
                {project.builder_email && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate" title={project.builder_email}>
                      {project.builder_email}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Project Details */}
            <div className="flex-grow">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardList className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <h4
                  className="text-xs font-medium text-gray-700 truncate"
                  title={project.project_name}
                >
                  {project.project_name || "Project Name N/A"}
                </h4>
              </div>
              <div className="space-y-1.5 text-xs text-gray-600 mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="truncate" title={project.project_location}>
                    {project.project_location || "Location N/A"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <IndianRupee className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span>{formatCurrency(project.project_budget)}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-2">
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-gray-400">Start</p>
                      <p className="font-medium">
                        {formatDate(project.starting_date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-gray-400">Deadline</p>
                      <p className="font-medium">
                        {formatDate(project.deadline_date)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-auto pt-3 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={() => handleAction(project, "decline")}
                disabled={processingProjectId === project.id}
                className="px-3 py-1.5 text-xs font-medium rounded-md text-red-600 bg-red-100 hover:bg-red-200 transition-colors flex items-center justify-center min-w-[80px] disabled:opacity-50"
              >
                {processingProjectId === project.id && statusLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <>
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Decline
                  </>
                )}
              </button>
              <button
                onClick={() => handleAction(project, "accept")}
                disabled={processingProjectId === project.id}
                className="px-3 py-1.5 text-xs font-medium rounded-md text-white bg-green-500 hover:bg-green-600 transition-colors flex items-center justify-center min-w-[80px] disabled:opacity-50"
              >
                {processingProjectId === project.id && statusLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Accept
                  </>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>
      {/* Floating Support Button */}=
    </div>
  );
};

export default ProjectInviteForm;
